import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { css } from '@emotion/css';
import { YtSharpRecommendation } from '../api';
import { YTVideoEmbed } from '../../../components/remark_directives/YTVideoEmbed';

import { CustomMarkdownRenderer } from '../../../components/CustomMarkdownRenderer';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { handleVideoClick } from '../utils/videoUtils';
import { useNavigate } from 'react-router-dom';
import sharpanaLogo from '../../../assets/sharpana_logo_bold.png';
import RecommendationActionsWrapper from './RecommendationActionsWrapper';
import { ResourceType, UserActivityType } from '../types/activity_types';
import * as Sentry from '@sentry/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown } from '@fortawesome/free-solid-svg-icons';
import { useRecommendationFeedStore } from '../../../stores/recommendationFeedStore';
import { handleSeeMoreLike, handleSeeLessDislike } from '../utils/recommendation_actions_utils';
import { storeUserActivity } from '../api_client/recommendation_api';

interface YtSharpProps {
  recommendation: YtSharpRecommendation;
  onboardingMode?: boolean;
  onUnbookmark?: () => void;
  hideMoreOptions?: boolean;
}

const INFO_MESSAGE = 'These summaries are AI generated and can sometimes represent the author\'s content inaccurately. Always double check the original content for any relevant purposes.';

const getYoutubeUrl = (videoId: string) => `https://www.youtube.com/watch?v=${videoId}`;

interface ActionButtonProps {
  onClick: () => void;
  className?: string;
}

const buttonStyles = css`
  background-color: #050505;
  border: 1px solid #666;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  color: #ffffff;
  padding: 8px 16px;
  height: auto;
  transition: all 0.2s ease;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: #fff;
    color: #e0e0e0;
  }

  &:active {
    background-color: #2a2a2a;
  }
`;

const buttonIconStyles = css`
  width: 20px;
  height: 20px;
  vertical-align: middle;
  flex-shrink: 0;
`;

const SharpanaButton: React.FC<ActionButtonProps> = ({ onClick, className }) => (
  <div
    className={`${buttonStyles} ${className}`}
    onClick={onClick}
  >
    <img src={sharpanaLogo} alt="Sharpana" className={buttonIconStyles} />
    <span>More insights</span>
  </div>
);

export const YouTubeLogo = () => (
  <svg 
    viewBox="0 0 30 20" 
    preserveAspectRatio="xMidYMid meet" 
    className={buttonIconStyles}
  >
    <g viewBox="0 0 30 20" preserveAspectRatio="xMidYMid meet">
      <g>
        <path 
          d="M27.9727 3.12324C27.6435 1.89323 26.6768 0.926623 25.4468 0.597366C23.2197 2.24288e-07 14.285 0 14.285 0C14.285 0 5.35042 2.24288e-07 3.12323 0.597366C1.89323 0.926623 0.926623 1.89323 0.597366 3.12324C2.24288e-07 5.35042 0 10 0 10C0 10 2.24288e-07 14.6496 0.597366 16.8768C0.926623 18.1068 1.89323 19.0734 3.12323 19.4026C5.35042 20 14.285 20 14.285 20C14.285 20 23.2197 20 25.4468 19.4026C26.6768 19.0734 27.6435 18.1068 27.9727 16.8768C28.5701 14.6496 28.5701 10 28.5701 10C28.5701 10 28.5677 5.35042 27.9727 3.12324Z" 
          fill="#FF0000"
        />
        <path d="M11.4253 14.2854L18.8477 10.0004L11.4253 5.71533V14.2854Z" fill="white"/>
      </g>
    </g>
  </svg>
);

const YouTubeButton: React.FC<ActionButtonProps> = ({ onClick, className }) => (
  <div
    className={`${buttonStyles} ${className}`}
    onClick={onClick}
  >
    <YouTubeLogo />
    <span>Watch full video</span>
  </div>
);

// The handleUserActivity function is now imported from recommendation_actions_utils.ts

// LastSlide component for the final slide with action buttons
interface LastSlideProps {
  recommendation: YtSharpRecommendation;
  onboardingMode?: boolean;
  navigate: ReturnType<typeof useNavigate>;
}

const LastSlide: React.FC<LastSlideProps> = ({ recommendation, onboardingMode = false, navigate }) => {
  const { 
    addUserActivity, 
    removeUserActivity, 
    isResourceLiked, 
    startCollapsingResource 
  } = useRecommendationFeedStore();
  const isLiked = recommendation.id ? isResourceLiked(recommendation.id) : false;
  
  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onboardingMode) return;
    
    await handleSeeMoreLike({
      resourceId: recommendation.id,
      resourceType: ResourceType.YT_SHARP,
      addUserActivity,
      removeUserActivity,
      isResourceLiked,
      onSuccess: undefined,
      derived_from_resource_id: recommendation.resource_id
    });
  };
  
  const handleDislike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onboardingMode) return;
    
    await handleSeeLessDislike({
      resourceId: recommendation.id,
      resourceType: ResourceType.YT_SHARP,
      addUserActivity,
      removeUserActivity,
      startCollapsingResource,
      derived_from_resource_id: recommendation.resource_id
    });
  };
  
  return (
    <div className={styles.actionsContainer}>
      <SharpanaButton
        className={styles.actionButton}
        onClick={() => {
          if (!onboardingMode) {
            handleVideoClick(recommendation.resource_id, navigate, true);
          }
        }}
      />
      <YouTubeButton
        className={styles.actionButton}
        onClick={() => {
          if (!onboardingMode) {
            window.open(getYoutubeUrl(recommendation.video_id), '_blank', 'noopener,noreferrer');
          }
        }}
      />
      <div className={styles.feedbackContainer}>
        <button 
          className={`${styles.feedbackButton} ${isLiked ? styles.activeFeedbackButton : ''}`}
          onClick={handleLike}
          disabled={onboardingMode}
        >
          <FontAwesomeIcon icon={faThumbsUp} className={styles.feedbackIcon} />
          <span>More like this</span>
        </button>
        <button 
          className={styles.feedbackButton}
          onClick={handleDislike}
          disabled={onboardingMode}
        >
          <FontAwesomeIcon icon={faThumbsDown} className={styles.feedbackIcon} />
          <span>Less like this</span>
        </button>
      </div>
    </div>
  );
};

// Container styles for the outer component
const containerStyles = css`
  position: relative;
  width: 100%;
  // background-color: #1a1a1a;
  background-color: #242424;
  border-radius: 8px;
  overflow: hidden;
  touch-action: pan-y;
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  min-height: 300px; /* Add minimum height to prevent layout jumping */
`;

// Carousel styles for the inner slider component
const carouselStyles = css`
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  will-change: transform;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

// Slide styles for each individual slide
const slideStyles = css`
  min-width: 100%;
  width: 100%;
  flex: 0 0 100%;
  flex-shrink: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

// Content styles for the content inside each slide
const contentStyles = css`
  padding: 1.1rem;
  color: #ffffff;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 769px) {
    padding: 1rem 8px;
  }
`;

const styles = {

  channelWrapper: css`
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    // flex-wrap: normal;

    @media (max-width: 769px) {
      max-width: 77%;
    }
  `,
  channelInfo: css`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    color: inherit;
    transition: opacity 0.2s;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      opacity: 0.8;
    }
  `,
  infoIcon: css`
    color: #666;
    font-size: 11px;
    transition: color 0.2s;
    margin-left: 8px;
    flex-shrink: 0;

    &:hover {
      color: #999;
    }
  `,
  channelAvatar: css`
    width: 26px;
    height: 26px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  channelName: css`
    font-size: 0.75rem;
    color: #a0a0a0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  title: css`
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #ffffff;
  `,
  actionsContainer: css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    justify-content: center;
    align-items: center;
    min-height: 100px;
    max-width: 300px;
    width: 100%;
    padding: 2rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  feedbackContainer: css`
    display: flex;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  `,
  feedbackButton: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background-color: #050505;
    border: 1px solid #666;
    border-radius: 4px;
    padding: 8px 16px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.9rem;
    
    &:hover {
      border-color: #fff;
      color: #e0e0e0;
      background-color: #1a1a1a;
    }
    
    &:active {
      background-color: #2a2a2a;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
  activeFeedbackButton: css`
    border-color: #1890ff;
    color: #1890ff;
    
    &:hover {
      border-color: #40a9ff;
      color: #40a9ff;
    }
  `,
  feedbackIcon: css`
    width: 16px;
    height: 16px;
  `,
  actionButton: css`
    min-width: 150px;
    width: 100%;
  `,
  text: css`
    font-size: 1rem;
    line-height: 1.5;
    color: #e0e0e0;
    margin-bottom: 1.5rem;
    width: 100%;
  `,
  navigation: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
  `,
  navButton: css`
    background: rgba(10, 10, 10, 0.55);
    border: none;
    color: #ffffff;
    cursor: pointer;
    padding: 0.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    margin: 7px;

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      opacity: 0.9;
      background: rgba(0, 0, 0, 0.7);
    }
  `,
  counter: css`
    color: #888;
    font-size: 0.875rem;
    margin: 0 1rem;
  `,
  arrow: css`
    width: 28px;
    height: 28px;
    fill: currentColor;
  `
};

export const YtSharp: React.FC<YtSharpProps> = ({ recommendation, onboardingMode = false, onUnbookmark, hideMoreOptions = false }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeDistance, setSwipeDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track whether all videos should be loaded (initially false, becomes true after first navigation)
  const [loadAllVideos, setLoadAllVideos] = useState(false);

  const SWIPE_THRESHOLD = 50; // Minimum distance to trigger swipe
  const TRANSITION_DURATION = 300; // milliseconds

  // Add action slide to the end of fragments
  const fragments = [
    ...recommendation.fragments,
    {
      title: '',
      content: '',
      isActionSlide: true
    }
  ];

  // Get current fragment
  const currentFragment = fragments[currentIndex];
  
  const isLastSlide = currentIndex === fragments.length - 1;
  const isFirstSlide = currentIndex === 0;
  
  const hasTrackedInteraction = useRef(false);

  // Track swipe direction to determine if we should block scrolling
  const swipeDirection = useRef<'horizontal' | 'vertical' | null>(null);

  // Force slide to snap to nearest position when animation ends
  useEffect(() => {
    if (!isDragging && swipeDistance !== 0) {
      // Ensure we're at a clean slide position
      setSwipeDistance(0);
    }
  }, [isDragging, swipeDistance]);

  // Add a touch end listener to handle edge cases
  useEffect(() => {
    const element = containerRef.current;
    
    const handleTouchEnd = () => {
      // Always ensure we snap to a slide when touch ends
      if (swipeDistance !== 0) {
        // Determine direction and snap
        if (swipeDistance < 0 && !isLastSlide) {
          const threshold = -SWIPE_THRESHOLD * 0.25;
          if (swipeDistance < threshold) {
            handleNext();
          }
        } else if (swipeDistance > 0 && !isFirstSlide) {
          const threshold = SWIPE_THRESHOLD * 0.25;
          if (swipeDistance > threshold) {
            handlePrevious();
          }
        }
        
        // Reset state
        setIsDragging(false);
        setSwipeDistance(0);
        swipeDirection.current = null;
      }
    };
    
    if (element) {
      element.addEventListener('touchend', handleTouchEnd);
      return () => {
        element.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isFirstSlide, isLastSlide, swipeDistance, containerRef]);
  
  // Simple helper to check if an element is part of the progress bar
  const hasProgressBarParent = (element: HTMLElement): boolean => {
    if (!element) return false;
    
    // Use closest() to efficiently check for any progress bar related elements
    // This replaces the recursive parent checks and multiple conditions
    return !!(
      element.closest('.progress-bar-isolator') || 
      element.closest('[data-no-swipe="true"]') || 
      element.closest('.ytSegmentProgressBar') || 
      element.closest('.progressBarContainer') || 
      element.closest('.progressBar') || 
      element.closest('.progressHandle') || 
      element.closest('.progressTime') || 
      element.closest('.current-time')
    );
  };

  // Simple filter to ignore progress bar events
  const eventFilter = (event: TouchEvent | MouseEvent) => {
    // Check if event originated from progress bar
    const target = event.target as HTMLElement;
    const isProgressBar = hasProgressBarParent(target);
    
    // If it's a progress bar event, reject it
    if (isProgressBar) {
      return false;
    }
    
    // For all other cases, allow useSwipeable to process the event
    return true;
  };

  const handlers = useSwipeable({
    // Pass our event filter to useSwipeable to reject progress bar interactions
    eventOptions: {
      passive: false,
      // This is the crucial part - filter events before they're processed
      filterTouch: eventFilter
    },
    onSwipeStart: (e) => {
      if (onboardingMode) return;
      swipeDirection.current = null;
    },
    onSwiping: (e) => {
      if (onboardingMode) return;
      
      // Always check for dominant direction to improve responsiveness 
      const isHorizontalMovement = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      
      // Update swipe direction only if it hasn't been set yet
      if (swipeDirection.current === null && 
          (Math.abs(e.deltaX) > 10 || Math.abs(e.deltaY) > 10)) {
        swipeDirection.current = isHorizontalMovement ? 'horizontal' : 'vertical';
      }
      
      // Only process horizontal swipes
      if ((swipeDirection.current === 'horizontal' || 
          (swipeDirection.current === null && isHorizontalMovement))) {
        // Prevent swiping if at the first/last slide
        if ((e.deltaX > 0 && isFirstSlide) || (e.deltaX < 0 && isLastSlide)) {
          // Allow a little resistance
          setSwipeDistance(e.deltaX / 3);
        } else {
          setSwipeDistance(e.deltaX);
        }
        setIsDragging(true);
      }
    },
    onSwiped: (e) => {
      // Skip if this swipe started on progress bar
      if (swipeDirection.current === 'blocked') {
        swipeDirection.current = null;
        return;
      }
      
      // For all swipes, ensure we handle horizontal movement
      // Even if there was a vertical component
      
      // If distance exceeds threshold, navigate
      if (swipeDistance < -SWIPE_THRESHOLD && !isLastSlide) {
        handleNext();
      } else if (swipeDistance > SWIPE_THRESHOLD && !isFirstSlide) {
        handlePrevious();
      } else if (swipeDistance !== 0) {
        // For shorter swipes, check if they should trigger navigation
        // Lower threshold for better responsiveness
        if (swipeDistance < -(SWIPE_THRESHOLD * 0.2) && !isLastSlide) {
          handleNext();
        } else if (swipeDistance > (SWIPE_THRESHOLD * 0.2) && !isFirstSlide) {
          handlePrevious();
        }
      }
      
      // Reset state
      setIsDragging(false);
      setSwipeDistance(0);
      swipeDirection.current = null;
    },
    preventDefaultTouchmoveEvent: (e) => {
      // Our new filter ensures progress bar events never get here
      // Just prevent default for horizontal swipes
      return swipeDirection.current === 'horizontal';
    },
    trackMouse: false,
    delta: 3, // Lower threshold for better responsiveness
    swipeDuration: 500 // Maximum time in ms for a swipe
  });

  const getSwipeStyle = () => {
    // Base position is negative of currentIndex multiplied by 100% (full width)
    // Plus any active swipe distance
    const basePosition = -currentIndex * 100;
    const translateX = `calc(${basePosition}% + ${swipeDistance}px)`;
    
    return {
      transform: `translateX(${translateX})`,
      transition: isDragging ? 'none' : `transform ${TRANSITION_DURATION}ms cubic-bezier(0.25, 0.1, 0.25, 1.0)`
    };
  };
  
  const handlePrevious = () => {
    if (isFirstSlide) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isLastSlide) return;
    setCurrentIndex((prev) => prev + 1);
    
    // Set loadAllVideos to true on the first navigation
    setLoadAllVideos(true);
  };

  // Track interaction when user reaches the last slide
  useEffect(() => {
    if (isLastSlide && !hasTrackedInteraction.current && recommendation.id) {
      // Mark as tracked immediately to prevent duplicate tracking attempts
      hasTrackedInteraction.current = true;
      
      // Use an async function inside useEffect
      const trackInteraction = async () => {
        try {
          // Ensure the activity is tracked with a timeout to prevent hanging
          await Promise.race([
            storeUserActivity(
              recommendation.id,
              ResourceType.YT_SHARP,
              UserActivityType.INTERACTED,
              recommendation.resource_id // Pass resource_id as derived_from_resource_id for YT_SHARP
            ),
            // Add a timeout as a fallback
            new Promise(resolve => setTimeout(resolve, 2000))
          ]);
        } catch (error) {
          console.error('Failed to track YtSharp interaction:', error);
          Sentry.captureException(error, {
            tags: { activity_type: UserActivityType.INTERACTED, resource_type: ResourceType.YT_SHARP, resource_id: recommendation.id }
          });
        }
      };
      
      // Execute the tracking function
      trackInteraction();
    }
  }, [isLastSlide, recommendation.id]);

  return (
    <RecommendationActionsWrapper 
      recommendation={recommendation}
      channelName={recommendation.channel_name}
      channelId={recommendation.channel_id}
      resourceId={recommendation.id}
      resourceType={ResourceType.YT_SHARP}
      onboardingMode={onboardingMode}
      onUnbookmark={onUnbookmark}
      hideMoreOptions={hideMoreOptions}
    >
      <div className={containerStyles} ref={containerRef}>
        
        
        {/* Carousel container */}
        <div 
          {...handlers}
          className={carouselStyles} 
          style={getSwipeStyle()}
        >
          {/* Render all slides in a row with translateX positioning them */}
          {fragments.map((fragment, index) => (
            <div key={index} className={slideStyles}>
              <div className={contentStyles}>
                <div className={styles.channelWrapper}>
                  {recommendation.channel_name && recommendation.channel_url && (
                    <>
                      <a 
                        href={recommendation.channel_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles.channelInfo}
                      >
                        {recommendation.channel_thumbnail && (
                          <div className={styles.channelAvatar}>
                            <img src={recommendation.channel_thumbnail} alt={recommendation.channel_name} />
                          </div>
                        )}
                        <span className={styles.channelName}>{recommendation.channel_name}</span>
                      </a>
                      <Tooltip title={INFO_MESSAGE} mouseEnterDelay={0.5}>
                        <InfoCircleOutlined className={styles.infoIcon} />
                      </Tooltip>
                    </>
                  )}
                </div>
                {fragment.title && (
                  <h2 className={styles.title}>{fragment.title}</h2>
                )}
                {fragment.isActionSlide ? (
                  <LastSlide
                    recommendation={recommendation}
                    onboardingMode={onboardingMode}
                    navigate={navigate}
                  />
                ) : (
                  <div className={styles.text}>
                    <CustomMarkdownRenderer rawMarkdownContent={fragment.content} />
                  </div>
                )}
                {fragment.video_start_time && fragment.video_end_time && (
                  (index < 2 || loadAllVideos) ? (
                    <YTVideoEmbed
                      videoId={recommendation.video_id}
                      startTime={fragment.video_start_time}
                      endTime={fragment.video_end_time}
                    />
                  ) : (
                    <div style={{ height: '280px', width: '100%' }} />
                  )
                )}
              </div>
            </div>
          ))
          }
        </div>
        
        {!onboardingMode && (
          <div className={styles.navigation}>
            <button
              className={styles.navButton}
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              <svg className={styles.arrow} viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
            <span className={styles.counter}>
              {currentIndex + 1} / {fragments.length}
            </span>
            <button
              className={styles.navButton}
              onClick={handleNext}
              disabled={isLastSlide}
            >
              <svg className={styles.arrow} viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </RecommendationActionsWrapper>
  );
};
